import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAvailableBrands } from "@/hooks/api/use-brand-ads";
import {
  useCreateProject,
  useDeleteProject,
  useProjects,
  useUpdateProject,
  type Project,
} from "@/hooks/api/use-projects";
import {
  Files,
  FileText,
  Link,
  MoreVertical,
  Pencil,
  Trash,
} from "lucide-react";
import { useState } from "react";
import { Link as RouterLink } from "react-router";
import { toast } from "sonner";

export default function ProjectsPage() {
  const { data: projects, isLoading } = useProjects();
  const { data: brands } = useAvailableBrands();
  const createProject = useCreateProject();
  const [open, setOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<number | null>(null);
  const [deletingProject, setDeletingProject] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState<string>();

  const updateProject = useUpdateProject(editingProject || 0);
  const deleteProject = useDeleteProject(deletingProject || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandId) return;

    try {
      if (editingProject) {
        await updateProject.mutateAsync({
          name,
          brand_id: parseInt(brandId),
        });
        toast.success("Project updated successfully");
      } else {
        await createProject.mutateAsync({
          name,
          brand_id: parseInt(brandId),
        });
        toast.success("Project created successfully");
      }
      setOpen(false);
      setEditingProject(null);
      setName("");
      setBrandId(undefined);
    } catch (error) {
      toast.error(
        editingProject ? "Failed to update project" : "Failed to create project"
      );
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project.id);
    setName(project.name);
    setBrandId(project.brand.id.toString());
    setOpen(true);
  };

  const handleDelete = async (projectId: number) => {
    setDeletingProject(projectId);
    try {
      await deleteProject.mutateAsync();
      toast.success("Project deleted successfully");
    } catch (error) {
      toast.error("Failed to delete project");
    } finally {
      setDeletingProject(null);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const handleCreateProject = () => {
    setOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Projects</h1>
        {projects && projects.length > 0 && (
          <Button onClick={handleCreateProject}>New Project</Button>
        )}
      </div>

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            setEditingProject(null);
            setName("");
            setBrandId(undefined);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingProject ? "Edit Project" : "Create New Project"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Project Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand">Brand</Label>
              <Select value={brandId} onValueChange={setBrandId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select a brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands?.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full">
              {editingProject ? "Update Project" : "Create Project"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {!projects?.length ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          <EmptyState
            title="No Projects Created"
            description="Create your first project to get started with managing your brand assets."
            icons={[FileText, Link, Files]}
            action={{
              label: "Create Project",
              onClick: handleCreateProject,
            }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="group relative p-6 rounded-xl border bg-gradient-to-br from-card to-card/40 text-card-foreground shadow-sm hover:shadow-lg transition-all duration-200 ease-in-out"
            >
              <RouterLink to={`/projects/${project.id}`} className="block">
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1.5">
                      <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                        {project.name}
                      </h2>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {project.brand.name}
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => e.preventDefault()}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            handleEdit(project);
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            handleDelete(project.id);
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </RouterLink>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
